import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Save, ArrowUp, ArrowDown, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AdminAPI, absoluteUrl } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';

const EMPTY = {
  name: '', title: '', company: '', video_url: '', video_type: 'mp4',
  thumbnail_url: '', quote: '', transcript: '', published: true, order: 0,
};

const VideoUploader = ({ value, onChange }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await AdminAPI.upload(file);
      onChange(absoluteUrl(res.url));
      toast({ title: 'Video uploaded', description: file.name });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setUploading(false); if (e.target) e.target.value = ''; }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white font-semibold text-sm btn-transition cursor-pointer">
        {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Play className="h-4 w-4" /> Upload MP4</>}
        <input type="file" accept="video/mp4,video/*" onChange={onFile} className="hidden" data-testid="tf-video-file" />
      </label>
    </div>
  );
};

const TestimonialForm = ({ open, onClose, editing, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { setF(editing ? { ...EMPTY, ...editing } : EMPTY); }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.video_url.trim()) {
      toast({ title: 'Missing fields', description: 'Name and video are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(), title: f.title.trim(), company: f.company.trim(),
        video_url: f.video_url.trim(), video_type: f.video_type,
        thumbnail_url: f.thumbnail_url.trim(), quote: f.quote.trim(),
        transcript: f.transcript.trim(), published: !!f.published, order: Number(f.order) || 0,
      };
      if (editing) await AdminAPI.updateTestimonial(editing.id, payload);
      else await AdminAPI.createTestimonial(payload);
      toast({ title: 'Saved', description: `“${payload.name}” ${editing ? 'updated' : 'created'}` });
      onSaved(); onClose();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save.' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="testimonial-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-[#050F2E]">{editing ? 'Edit testimonial' : 'New testimonial'}</DialogTitle>
          <DialogDescription>Add an approved customer video and verified identity.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name *</Label>
              <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tf-name" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Title</Label>
              <Input value={f.title} onChange={(e) => set('title', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tf-title" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Company</Label>
              <Input value={f.company} onChange={(e) => set('company', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tf-company" /></div>
          </div>

          <div className="grid grid-cols-4 gap-3 items-end">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Video type</Label>
              <select value={f.video_type} onChange={(e) => set('video_type', e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold" data-testid="tf-video-type">
                <option value="mp4">MP4 file</option>
                <option value="youtube">YouTube</option>
              </select></div>
            <div className="col-span-3"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Video URL *</Label>
              <Input value={f.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder={f.video_type === 'youtube' ? 'https://youtu.be/...' : 'https://.../video.mp4'} className="mt-1.5 h-11 rounded-xl" data-testid="tf-video-url" /></div>
          </div>
          {f.video_type === 'mp4' && <VideoUploader value={f.video_url} onChange={(v) => set('video_url', v)} />}

          <ImageUploader label="Thumbnail" value={f.thumbnail_url} onChange={(v) => set('thumbnail_url', v)} testId="tf-thumb" />

          <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Short quote</Label>
            <Textarea value={f.quote} onChange={(e) => set('quote', e.target.value)} rows={2} className="mt-1.5 rounded-xl resize-none" data-testid="tf-quote" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Transcript / captions</Label>
            <Textarea value={f.transcript} onChange={(e) => set('transcript', e.target.value)} rows={3} className="mt-1.5 rounded-xl resize-none" data-testid="tf-transcript" /></div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={f.published} onChange={(e) => set('published', e.target.checked)} data-testid="tf-published" /> Published
            </label>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Order</Label>
              <Input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} className="h-9 w-24 rounded-lg" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full h-11 px-6 font-bold">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="tf-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminTestimonials = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await AdminAPI.listTestimonials()); }
    catch { toast({ title: 'Error', description: 'Could not load testimonials.' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await AdminAPI.reorderTestimonials(next.map((t) => t.id)); }
    catch { toast({ title: 'Error', description: 'Could not reorder.' }); load(); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete testimonial from “${name}”?`)) return;
    try { await AdminAPI.deleteTestimonial(id); toast({ title: 'Deleted', description: name }); load(); }
    catch { toast({ title: 'Error', description: 'Could not delete.' }); }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-[#050F2E] tracking-tight">Video testimonials</h1>
          <p className="text-slate-500 mt-1">{items.length} testimonials · drag order with the arrows</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="admin-new-testimonial">
          <Plus className="h-4 w-4 mr-2" /> New testimonial
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t, idx) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`admin-testimonial-${t.id}`}>
              <div className="relative aspect-video bg-slate-100">
                {t.thumbnail_url ? <img src={absoluteUrl(t.thumbnail_url)} alt={t.name} className="w-full h-full object-cover" /> : null}
                <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${t.published ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                  {t.published ? 'Published' : 'Hidden'}
                </span>
              </div>
              <div className="p-4">
                <p className="font-bold text-[#050F2E]">{t.name}</p>
                <p className="text-xs text-slate-500">{[t.title, t.company].filter(Boolean).join(' · ')}</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(idx, -1)} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid={`up-${t.id}`}><ArrowUp className="h-4 w-4" /></button>
                    <button onClick={() => move(idx, 1)} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid={`down-${t.id}`}><ArrowDown className="h-4 w-4" /></button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditing(t); setFormOpen(true); }} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-[#0A2A63] flex items-center justify-center" data-testid={`edit-t-${t.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(t.id, t.name)} className="h-8 w-8 rounded-lg hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`delete-t-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TestimonialForm open={formOpen} editing={editing} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
};

export default AdminTestimonials;
